import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface StrandedItem {
  sku: string;
  asin: string;
  title: string;
  reason: string;
  strandedDate: string | null;
  availableQty: number;
  fixInstructions: string;
}

interface Account {
  id: string;
  name: string;
}

// ── Component ────────────────────────────────────────────────────────────────

export function InventoryStranded() {
  const [items, setItems] = useState<StrandedItem[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { displayName } = useNicknames();

  useEffect(() => {
    api.get<Account[]>('/accounts').then((accts) => {
      setAccounts(accts);
      if (accts.length > 0) setSelectedAccount(accts[0].id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedAccount) return;
    setLoading(true);
    setError('');

    api.get<{ totalStranded: number; items: StrandedItem[] }>(`/inventory/stranded/${selectedAccount}`)
      .then((data) => setItems(data.items))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [selectedAccount]);

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Stranded Inventory</h2>
          <p className="text-sm text-gray-500">FBA items without an active listing — unable to sell until fixed.</p>
        </div>
        {accounts.length > 1 && (
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Warning banner */}
      {items.length > 0 && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <p className="text-sm font-medium text-orange-800">
              {items.length} stranded item{items.length !== 1 ? 's' : ''} — these cannot be sold until fixed.
            </p>
          </div>
        </div>
      )}

      {/* Stranded Table */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        {items.length === 0 ? (
          <div className="flex flex-col items-center py-12">
            <svg className="h-12 w-12 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-3 text-sm font-medium text-gray-900">All inventory is active</p>
            <p className="mt-1 text-xs text-gray-500">No stranded items detected. Great job!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">SKU</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">ASIN</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Title</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Reason</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Stranded Date</th>
                  <th className="pb-2 text-right text-xs font-medium text-gray-500">Qty</th>
                  <th className="pb-2 text-left text-xs font-medium text-gray-500">Fix</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((item) => (
                  <tr key={item.sku}>
                    <td className="py-2 max-w-[120px] truncate font-medium text-gray-900" title={item.sku}>{displayName(item.sku)}</td>
                    <td className="py-2 text-gray-600">{item.asin}</td>
                    <td className="py-2 max-w-[180px] truncate text-gray-600" title={item.title}>{item.title}</td>
                    <td className="py-2 max-w-[160px] truncate text-orange-600" title={item.reason}>{item.reason}</td>
                    <td className="py-2 text-gray-500">{item.strandedDate?.slice(0, 10) ?? '—'}</td>
                    <td className="py-2 text-right font-medium text-gray-900">{item.availableQty}</td>
                    <td className="py-2 max-w-[220px] text-xs text-gray-600">{item.fixInstructions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-5 w-36 rounded bg-gray-200" />
          <div className="mt-2 h-4 w-64 rounded bg-gray-200" />
        </div>
      </div>
      <div className="h-48 rounded-xl bg-gray-200" />
    </div>
  );
}
